// ─── Reliability, at the interface ───────────────────────────────────────────
//
// `vision/overview.md` extends the Reliability principle past backend claims:
//
//   "a genuine zero/all-clear state must be visually distinguishable from data
//    that's simply missing or not yet loaded, and 'still loading' must be
//    visually distinguishable from 'loaded and verified' — a user should never
//    be able to mistake incomplete data for a real current state just by
//    glancing at a screen."
//
// The ordinary React habit is `{items.length === 0 && <Empty/>}`, which renders
// exactly the same thing for "there are genuinely no urgent tasks" and "the
// request failed and we have nothing". On a show field at 9am those two mean
// opposite things. `DataState` exists so a page cannot accidentally express
// one as the other: there is no way to render a list through it without
// saying which of the four states it is in.

import type { ReactNode } from "react";

export type LoadState<T> =
  | { status: "loading" }
  /** Loaded, and the answer really is none. This is a positive result. */
  | { status: "empty" }
  | { status: "ready"; data: T }
  /** We asked and could not find out. NOT the same as none. */
  | { status: "unavailable"; reason?: string };

export function DataState<T>({
  state, children, emptyMessage = "Nothing here", label,
}: {
  state: LoadState<T>;
  children: (data: T) => ReactNode;
  /** Phrase this as a confirmed all-clear ("No urgent tasks"), not as an
   *  absence of information. */
  emptyMessage?: string;
  label: string;
}) {
  if (state.status === "loading") {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={`${label}: loading`}
        className="flex items-center gap-2.5 px-4 py-5 text-sm nl-animated"
        style={{ color: "var(--nl-ink-faint)" }}
      >
        {/* Skeleton bars, deliberately unlike the tick used for a verified
         *  zero — a glance has to separate them. */}
        <span className="inline-block h-3 w-24 animate-pulse rounded" style={{ background: "var(--nl-track)" }} />
        <span className="inline-block h-3 w-12 animate-pulse rounded" style={{ background: "var(--nl-track)" }} />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (state.status === "unavailable") {
    return (
      <div
        role="status"
        aria-label={`${label}: unavailable`}
        className="flex items-center gap-2.5 px-4 py-5 text-sm"
        style={{ color: "var(--nl-ink-dim)" }}
      >
        <span aria-hidden className="text-base">⚠</span>
        {/* Says what is actually true: we do not know. Never a zero. */}
        <span>Couldn’t load {label.toLowerCase()}{state.reason ? ` — ${state.reason}` : ""}. This isn’t “none”.</span>
      </div>
    );
  }

  if (state.status === "empty") {
    return (
      <div
        role="status"
        aria-label={`${label}: none`}
        className="flex items-center gap-2.5 px-4 py-5 text-sm"
        style={{ color: "var(--nl-ink-dim)" }}
      >
        <span aria-hidden style={{ color: "var(--nl-status-on)" }}>✓</span>
        <span>{emptyMessage}</span>
      </div>
    );
  }

  return <>{children(state.data)}</>;
}

/**
 * A live number that knows whether it is real.
 *
 * The same problem as DataState in miniature: a tile showing "0" when the
 * fetch failed is a confident false statement. An unknown value shows a dash.
 */
export function Metric({ label, value, state = "ready" }: {
  label: string;
  value?: number | string;
  state?: "ready" | "loading" | "unavailable";
}) {
  const display = state === "ready" && value !== undefined ? value : state === "loading" ? "…" : "—";
  return (
    <span className="text-sm" style={{ color: "var(--nl-ink-dim)" }}>
      {label}:{" "}
      <span style={{ color: state === "ready" && value !== undefined ? "var(--nl-ink)" : "var(--nl-ink-faint)" }}>
        {display}
      </span>
    </span>
  );
}

export type SyncState = "pending" | "confirmed" | "failed";

/**
 * Whether an action has actually reached the server.
 *
 * "An action taken while offline must queue for sync rather than the UI
 * pretending it already reached the server or the intended person;
 * 'pending/queued' and 'confirmed' must be visibly distinct states, handled
 * subtly and calmly rather than as an alarming error."
 *
 * Hence: distinct but quiet. Pending is a soft outline dot, confirmed is a
 * filled tick, failed is muted rather than red-alarming — the person is
 * standing in a field with no signal, which is normal, not a crisis. `failed`
 * still says "not sent" rather than going silent, because silence would read
 * as success.
 */
export function SyncBadge({ state, className = "" }: { state: SyncState; className?: string }) {
  const map = {
    pending:   { mark: "○", text: "Queued",    colour: "var(--nl-pending)" },
    confirmed: { mark: "✓", text: "Sent",      colour: "var(--nl-confirmed)" },
    failed:    { mark: "↻", text: "Not sent",  colour: "var(--nl-status-available)" },
  }[state];
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${className}`}
      style={{ color: map.colour }}
      aria-label={`Sync state: ${map.text}`}
    >
      <span aria-hidden>{map.mark}</span>
      <span>{map.text}</span>
    </span>
  );
}

/** The four confirmed operational states. Never derived from a schedule —
 *  `system-logic/overview.md` is explicit that a scheduled duty makes someone
 *  expected, not confirmed, so this component only ever renders state that was
 *  actually declared. */
export type OperationalStatus = "off_duty" | "on_duty" | "available" | "needs_attention";

const STATUS_META: Record<OperationalStatus, { label: string; colour: string }> = {
  off_duty:        { label: "Off Duty",        colour: "var(--nl-status-off)" },
  on_duty:         { label: "On Duty",         colour: "var(--nl-status-on)" },
  available:       { label: "Available",       colour: "var(--nl-status-available)" },
  needs_attention: { label: "Needs Attention", colour: "var(--nl-status-attention)" },
};

export function StatusDot({ status, size = 16 }: { status: OperationalStatus; size?: number }) {
  const meta = STATUS_META[status];
  return (
    <span
      role="img"
      aria-label={meta.label}
      title={meta.label}
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: meta.colour }}
    />
  );
}

export function StatusLine({ status, detail, message }: {
  status: OperationalStatus;
  /** e.g. the Location they're at. */
  detail?: string;
  /** "A status message can report an issue without flipping status to red." */
  message?: string;
}) {
  const meta = STATUS_META[status];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatusDot status={status} />
      <span style={{ color: "var(--nl-ink)" }}>{meta.label}</span>
      {detail && <span style={{ color: "var(--nl-ink-dim)" }}>: {detail}</span>}
      {message && (
        <span className="text-sm" style={{ color: "var(--nl-ink-dim)" }}>— {message}</span>
      )}
    </div>
  );
}

export function statusLabel(status: OperationalStatus): string {
  return STATUS_META[status].label;
}
